/**
 * preview-store — tracks which task (if any) is open in the preview drawer.
 *
 * A tiny Zustand store rather than route/search state: opening a preview is
 * a transient UI action, not a navigation, and shouldn't push history entries
 * or round-trip through the router.
 *
 * Unsaved-edit guarding: the drawer's description editor reports `dirty` while
 * it holds unsaved changes. `open`/`close` won't discard a dirty draft — they
 * stash the intent (`pendingTaskId` / `pendingClose`) so the drawer can show a
 * confirm dialog first. `confirmPending` applies the stashed intent (discard),
 * `cancelPending` drops it (keep editing). `reset` closes unconditionally and
 * is used on real route changes, where the navigation was already confirmed.
 */

import { create } from 'zustand';

interface PreviewState {
  taskId: string | null;
  /** True while the drawer's description editor holds unsaved changes. */
  dirty: boolean;
  /** A task-switch requested while dirty, awaiting confirmation. */
  pendingTaskId: string | null;
  /** A close requested while dirty, awaiting confirmation. */
  pendingClose: boolean;
  open: (id: string) => void;
  close: () => void;
  /** Unconditional close — used on real route changes (nav already happened). */
  reset: () => void;
  setDirty: (dirty: boolean) => void;
  /** User confirmed discarding changes → apply the pending intent. */
  confirmPending: () => void;
  /** User chose to keep editing → drop the pending intent. */
  cancelPending: () => void;
}

export const usePreviewStore = create<PreviewState>((set, get) => ({
  taskId: null,
  dirty: false,
  pendingTaskId: null,
  pendingClose: false,
  open: (id) => {
    const { taskId, dirty } = get();
    if (id === taskId) return;
    if (dirty) {
      set({ pendingTaskId: id, pendingClose: false });
      return;
    }
    set({ taskId: id });
  },
  close: () => {
    if (get().dirty) {
      set({ pendingClose: true, pendingTaskId: null });
      return;
    }
    set({ taskId: null, dirty: false, pendingTaskId: null, pendingClose: false });
  },
  reset: () =>
    set({ taskId: null, dirty: false, pendingTaskId: null, pendingClose: false }),
  setDirty: (dirty) => set({ dirty }),
  confirmPending: () => {
    const { pendingTaskId } = get();
    set({
      taskId: pendingTaskId ?? null,
      pendingTaskId: null,
      pendingClose: false,
      dirty: false,
    });
  },
  cancelPending: () => set({ pendingTaskId: null, pendingClose: false }),
}));
